import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { SearchIcon, PlusIcon, CalendarIcon, ClockIcon, UsersIcon, EditIcon, TrashIcon, MoreVerticalIcon, ImageIcon, AwardIcon, GlobeIcon, TagIcon, FilterIcon, XIcon } from 'lucide-react';
import { AgendaFeature, AgendaItem } from '../../../types/agenda';
import { sessions } from '../../../data/sessions';
import { Session } from '../../../types';
import { AgendaFeatureDetail } from './AgendaFeatureDetail';
interface AgendaManagementProps {
  onBack: () => void;
  onFeatureSelect?: (feature: AgendaFeature | null) => void;
  onRegisterExitHandler?: (handler: () => void) => void;
}
// Mock sub-events data (in production, this would come from context or props)
const SUB_EVENTS = [{
  id: 'muscle',
  name: 'Dubai Muscle Show',
  shortName: 'DMS',
  color: '#8B5CF6'
}, {
  id: 'active',
  name: 'Dubai Active Show',
  shortName: 'DAS',
  color: '#3B82F6'
}, {
  id: 'industry',
  name: 'Dubai Active Industry',
  shortName: 'DAI',
  color: '#10B981'
}];
// Convert Session to AgendaItem
const sessionToAgendaItem = (session: Session): AgendaItem => {
  const dateStr = session.date;
  const startTime = session.startTime;
  const endTime = session.endTime;
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const startAt = `${year}-${month}-${day}T${startTime}:00+04:00`;
  const endAt = `${year}-${month}-${day}T${endTime}:00+04:00`;
  return {
    id: session.id,
    startAt,
    endAt,
    title: session.title,
    description: {
      type: 'html',
      value: session.description
    },
    image: session.image ? {
      url: session.image
    } : undefined,
    speakerGroups: session.speakers ? [{
      id: 'speakers-1',
      title: 'Speakers',
      speakers: session.speakers.map((s, idx) => ({
        id: `speaker-${idx}`,
        speaker: {
          id: s.name
        }
      }))
    }] : session.speaker ? [{
      id: 'speaker-1',
      title: 'Speaker',
      speakers: [{
        id: 'speaker-1',
        speaker: {
          id: session.speaker
        }
      }]
    }] : undefined
  };
};
// Group sessions by feature
const groupSessionsByFeature = (): AgendaFeature[] => {
  const featureMap = new Map<string, {
    name: string;
    sessions: Session[];
  }>();
  sessions.forEach(session => {
    if (!featureMap.has(session.feature)) {
      const featureName = session.feature.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      featureMap.set(session.feature, {
        name: featureName,
        sessions: []
      });
    }
    featureMap.get(session.feature)!.sessions.push(session);
  });
  return Array.from(featureMap.entries()).map(([featureId, data]) => ({
    id: featureId,
    name: data.name,
    shortName: featureId.toUpperCase().replace(/-/g, ''),
    subEventIds: [data.sessions[0].eventId],
    items: data.sessions.map(sessionToAgendaItem)
  }));
};
export function AgendaManagement({
  onBack,
  onFeatureSelect,
  onRegisterExitHandler
}: AgendaManagementProps) {
  const [features, setFeatures] = useState<AgendaFeature[]>(() => groupSessionsByFeature());
  const [selectedFeature, setSelectedFeature] = useState<AgendaFeature | null>(null);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState<AgendaFeature | null>(null);
  const [activeActionMenu, setActiveActionMenu] = useState<string | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubEvents, setSelectedSubEvents] = useState<string[]>([]);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  // Feature form state
  const [featureName, setFeatureName] = useState('');
  const [featureShortName, setFeatureShortName] = useState('');
  const [featureChair, setFeatureChair] = useState('');
  const [featureSubEvents, setFeatureSubEvents] = useState<string[]>([]);
  // Register exit handler with parent
  useEffect(() => {
    if (onRegisterExitHandler) {
      onRegisterExitHandler(() => {
        setSelectedFeature(null);
        if (onFeatureSelect) {
          onFeatureSelect(null);
        }
      });
    }
  }, [onRegisterExitHandler, onFeatureSelect]);
  // Close actions menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        setActiveActionMenu(null);
      }
    };
    if (activeActionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [activeActionMenu]);
  // Close filters dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target as Node)) {
        setShowFiltersDropdown(false);
      }
    };
    if (showFiltersDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showFiltersDropdown]);
  const clearFilters = () => {
    setSelectedSubEvents([]);
  };
  const activeFiltersCount = selectedSubEvents.length;
  const filteredFeatures = features.filter(feature => {
    const matchesSearch = searchQuery === '' || feature.name.toLowerCase().includes(searchQuery.toLowerCase()) || feature.shortName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSubEvent = selectedSubEvents.length === 0 || feature.subEventIds.some(id => selectedSubEvents.includes(id)) || feature.subEventIds.length === 0 && selectedSubEvents.length > 0;
    return matchesSearch && matchesSubEvent;
  });
  const handleOpenFeatureModal = (feature?: AgendaFeature) => {
    if (feature) {
      setEditingFeature(feature);
      setFeatureName(feature.name);
      setFeatureShortName(feature.shortName);
      setFeatureChair(feature.chairedBy || '');
      setFeatureSubEvents(feature.subEventIds);
    } else {
      setEditingFeature(null);
      setFeatureName('');
      setFeatureShortName('');
      setFeatureChair('');
      setFeatureSubEvents([]);
    }
    setShowFeatureModal(true);
  };
  const handleSaveFeature = () => {
    if (editingFeature) {
      setFeatures(features.map(f => f.id === editingFeature.id ? {
        ...f,
        name: featureName,
        shortName: featureShortName,
        chairedBy: featureChair || undefined,
        subEventIds: featureSubEvents
      } : f));
    } else {
      const newFeature: AgendaFeature = {
        id: `feature-${Date.now()}`,
        name: featureName,
        shortName: featureShortName,
        chairedBy: featureChair || undefined,
        subEventIds: featureSubEvents,
        items: []
      };
      setFeatures([...features, newFeature]);
    }
    setShowFeatureModal(false);
  };
  const handleDeleteFeature = (featureId: string) => {
    setFeatures(features.filter(f => f.id !== featureId));
    setActiveActionMenu(null);
  };
  const handleFeatureClick = (feature: AgendaFeature) => {
    setSelectedFeature(feature);
    if (onFeatureSelect) {
      onFeatureSelect(feature);
    }
  };
  const handleSaveFeatureDetail = (updatedFeature: Partial<AgendaFeature>) => {
    if (!selectedFeature) return;
    setFeatures(features.map(f => f.id === selectedFeature.id ? {
      ...f,
      ...updatedFeature
    } : f));
    // Update selected feature with new data
    setSelectedFeature({
      ...selectedFeature,
      ...updatedFeature
    });
  };
  const toggleSubEvent = (subEventId: string) => {
    setFeatureSubEvents(prev => prev.includes(subEventId) ? prev.filter(id => id !== subEventId) : [...prev, subEventId]);
  };
  const toggleSubEventFilter = (subEventId: string) => {
    setSelectedSubEvents(prev => prev.includes(subEventId) ? prev.filter(id => id !== subEventId) : [...prev, subEventId]);
  };
  const getSubEventBadges = (subEventIds: string[]) => {
    if (subEventIds.length === 0) {
      return <Badge variant="default" className="bg-gray-100 text-gray-700" data-id="element-596">
          <GlobeIcon className="w-3 h-3 mr-1" data-id="element-597" />
          Global
        </Badge>;
    }
    return subEventIds.map(id => {
      const subEvent = SUB_EVENTS.find(se => se.id === id);
      if (!subEvent) return null;
      return <Badge key={id} className="text-white" style={{
        backgroundColor: subEvent.color
      }} data-id="element-598">
          {subEvent.shortName}
        </Badge>;
    });
  };
  // If feature is selected, show detail view
  if (selectedFeature) {
    return <AgendaFeatureDetail feature={selectedFeature} onBack={() => {
      setSelectedFeature(null);
      if (onFeatureSelect) {
        onFeatureSelect(null);
      }
    }} onSave={handleSaveFeatureDetail} data-id="element-599" />;
  }
  // FEATURES LIST VIEW
  return <div className="space-y-6" data-id="element-600">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-601">
        <div data-id="element-602">
          <h1 className="text-2xl font-bold text-gray-900 font-inter" data-id="element-603">
            Agenda Features
          </h1>
          <p className="text-gray-500 font-inter mt-1" data-id="element-604">
            Manage agenda features and their sub-event assignments
          </p>
        </div>
        <Button variant="primary" onClick={() => handleOpenFeatureModal()} data-id="element-605">
          <PlusIcon className="w-4 h-4 mr-2" data-id="element-606" />
          Add Feature
        </Button>
      </div>

      {/* Enhanced Filters Bar */}
      <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-607">
        <div className="flex gap-4 items-start" data-id="element-608">
          {/* Search */}
          <div className="relative flex-1 max-w-md" data-id="element-609">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-610" />
            <input type="text" placeholder="Search features..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-inter text-sm" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-611" />
          </div>

          {/* Quick Filters - Sub-Events */}
          <div className="flex gap-2 flex-wrap" data-id="element-612">
            {SUB_EVENTS.map(subEvent => <button key={subEvent.id} onClick={() => toggleSubEventFilter(subEvent.id)} className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${selectedSubEvents.includes(subEvent.id) ? 'border-blue-200 text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} style={selectedSubEvents.includes(subEvent.id) ? {
            backgroundColor: subEvent.color,
            borderColor: subEvent.color
          } : {}} data-id="element-613">
                {subEvent.shortName}
              </button>)}
          </div>

          {/* More Filters Dropdown */}
          <div className="relative" ref={filtersRef} data-id="element-614">
            <Button variant="outline" size="sm" onClick={() => setShowFiltersDropdown(!showFiltersDropdown)} data-id="element-615">
              <FilterIcon className="w-4 h-4 mr-2" data-id="element-616" />
              More Filters
              {activeFiltersCount > 0 && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full" data-id="element-617">
                  {activeFiltersCount}
                </span>}
            </Button>

            {showFiltersDropdown && <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[500px] overflow-y-auto" data-id="element-618">
                <div className="p-4" data-id="element-619">
                  <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-2 border-b border-gray-100" data-id="element-620">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-621">
                      Advanced Filters
                    </h3>
                    {activeFiltersCount > 0 && <button onClick={clearFilters} className="text-xs text-blue-600 hover:text-blue-700" data-id="element-622">
                        Clear all
                      </button>}
                  </div>

                  {/* Sub-Event Filter */}
                  <div data-id="element-623">
                    <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-624">
                      Sub-Events
                    </label>
                    <div className="space-y-2" data-id="element-625">
                      {SUB_EVENTS.map(subEvent => <label key={subEvent.id} className="flex items-center gap-2 cursor-pointer" data-id="element-626">
                          <input type="checkbox" checked={selectedSubEvents.includes(subEvent.id)} onChange={() => toggleSubEventFilter(subEvent.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-627" />
                          <div className="w-3 h-3 rounded" style={{
                      backgroundColor: subEvent.color
                    }} data-id="element-628" />
                          <span className="text-sm text-gray-700" data-id="element-629">
                            {subEvent.name}
                          </span>
                        </label>)}
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>

        {/* Active Filters Summary */}
        {activeFiltersCount > 0 && <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2 flex-wrap" data-id="element-630">
            <span className="text-xs font-medium text-gray-500" data-id="element-631">
              Active filters:
            </span>
            {selectedSubEvents.map(id => {
          const subEvent = SUB_EVENTS.find(se => se.id === id);
          if (!subEvent) return null;
          return <span key={id} className="px-2 py-1 text-xs rounded flex items-center gap-1 text-white" style={{
            backgroundColor: subEvent.color
          }} data-id="element-632">
                  {subEvent.shortName}
                  <button onClick={() => toggleSubEventFilter(id)} className="hover:opacity-80" data-id="element-633">
                    <XIcon className="w-3 h-3" data-id="element-634" />
                  </button>
                </span>;
        })}
          </div>}
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-635">
        {filteredFeatures.map(feature => <Card key={feature.id} className="cursor-pointer hover:shadow-lg transition-all duration-200 group relative" padding="lg" onClick={() => handleFeatureClick(feature)} data-id="element-636">
            {/* Action Menu Button */}
            <div className="absolute top-4 right-4 z-10" data-id="element-637">
              <button data-feature-id={feature.id} onClick={e => {
            e.stopPropagation();
            setActiveActionMenu(activeActionMenu === feature.id ? null : feature.id!);
          }} className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-638">
                <MoreVerticalIcon className="w-5 h-5" data-id="element-639" />
              </button>
            </div>

            {/* Feature Content */}
            <div className="mb-4" data-id="element-640">
              <div className="flex items-center gap-3 mb-3" data-id="element-641">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-xl" data-id="element-642">
                  {feature.shortName.charAt(0)}
                </div>
                <div className="flex-1" data-id="element-643">
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors" data-id="element-644">
                    {feature.name}
                  </h3>
                  <div className="text-xs text-gray-500 font-mono" data-id="element-645">
                    {feature.shortName}
                  </div>
                </div>
              </div>

              {feature.chairedBy && <p className="text-sm text-gray-600 mb-3" data-id="element-646">
                  {feature.chairedBy}
                </p>}

              {/* Sub-Event Badges */}
              <div className="flex flex-wrap gap-2 mb-4" data-id="element-647">
                {getSubEventBadges(feature.subEventIds)}
              </div>
            </div>

            {/* Stats */}
            <div className="pt-4 border-t border-gray-200 flex items-center justify-between text-sm" data-id="element-648">
              <div className="flex items-center gap-1 text-gray-600" data-id="element-649">
                <CalendarIcon className="w-4 h-4" data-id="element-650" />
                <span data-id="element-651">
                  {feature.items.length} session
                  {feature.items.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="text-blue-600 font-medium group-hover:translate-x-1 transition-transform" data-id="element-652">
                Manage →
              </div>
            </div>
          </Card>)}
      </div>

      {filteredFeatures.length === 0 && <div className="bg-white rounded-lg border border-gray-200 p-12 text-center" data-id="element-653">
          <TagIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" data-id="element-654" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-655">
            No features found
          </h3>
          <p className="text-gray-500 mb-4" data-id="element-656">
            {searchQuery || activeFiltersCount > 0 ? 'Try adjusting your filters' : 'Get started by adding your first feature'}
          </p>
          {!searchQuery && activeFiltersCount === 0 && <Button variant="primary" onClick={() => handleOpenFeatureModal()} data-id="element-657">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-658" />
              Add Feature
            </Button>}
        </div>}

      {/* Actions Menu */}
    </div>;
}